// Unit Categories
export type UnitCategory = "mass" | "volume" | "count" | "length" | "custom";

// Base Units for each category
export const BaseUnits: Record<UnitCategory, string> = {
  mass: "g",
  volume: "ml",
  count: "pcs",
  length: "cm",
  custom: "custom",
};

// Unit Definition Interface
export interface UnitDefinition {
  symbol: string;
  name: string;
  category: UnitCategory;
  conversionFactor: number; // Conversion factor to base unit
  commonUse?: boolean; // Whether this unit is commonly used
  alternateSymbols?: string[]; // Alternative symbols for the same unit
}

// Unit Definitions
export const UnitDefinitions: Record<string, UnitDefinition> = {
  // Mass Units
  mg: {
    symbol: "mg",
    name: "Milligram",
    category: "mass",
    conversionFactor: 0.001,
    commonUse: true,
    alternateSymbols: ["milligram", "milligrams"],
  },
  g: {
    symbol: "g",
    name: "Gram",
    category: "mass",
    conversionFactor: 1,
    commonUse: true,
    alternateSymbols: ["gram", "grams"],
  },
  kg: {
    symbol: "kg",
    name: "Kilogram",
    category: "mass",
    conversionFactor: 1000,
    commonUse: true,
    alternateSymbols: ["kilogram", "kilograms"],
  },
  oz: {
    symbol: "oz",
    name: "Ounce",
    category: "mass",
    conversionFactor: 28.3495,
    commonUse: true,
    alternateSymbols: ["ounce", "ounces"],
  },
  lb: {
    symbol: "lb",
    name: "Pound",
    category: "mass",
    conversionFactor: 453.592,
    commonUse: true,
    alternateSymbols: ["pound", "pounds"],
  },

  // Volume Units
  ml: {
    symbol: "ml",
    name: "Milliliter",
    category: "volume",
    conversionFactor: 1,
    commonUse: true,
    alternateSymbols: ["milliliter", "milliliters", "cc", "mL"],
  },
  l: {
    symbol: "l",
    name: "Liter",
    category: "volume",
    conversionFactor: 1000,
    commonUse: true,
    alternateSymbols: ["liter", "liters", "L"],
  },
  tsp: {
    symbol: "tsp",
    name: "Teaspoon",
    category: "volume",
    conversionFactor: 4.92892,
    commonUse: true,
    alternateSymbols: ["teaspoon", "teaspoons"],
  },
  tbsp: {
    symbol: "tbsp",
    name: "Tablespoon",
    category: "volume",
    conversionFactor: 14.7868,
    commonUse: true,
    alternateSymbols: ["tablespoon", "tablespoons"],
  },
  cup: {
    symbol: "cup",
    name: "Cup",
    category: "volume",
    conversionFactor: 236.588,
    commonUse: true,
    alternateSymbols: ["cups"],
  },
  fl_oz: {
    symbol: "fl_oz",
    name: "Fluid Ounce",
    category: "volume",
    conversionFactor: 29.5735,
    commonUse: true,
    alternateSymbols: ["fluid ounce", "fluid ounces"],
  },
  gal: {
    symbol: "gal",
    name: "Gallon",
    category: "volume",
    conversionFactor: 3785.41,
    commonUse: true,
    alternateSymbols: ["gallon", "gallons"],
  },

  // Count Units
  pcs: {
    symbol: "pcs",
    name: "Pieces",
    category: "count",
    conversionFactor: 1,
    commonUse: true,
    alternateSymbols: ["piece", "pieces"],
  },
  dozen: {
    symbol: "dozen",
    name: "Dozen",
    category: "count",
    conversionFactor: 12,
    commonUse: true,
    alternateSymbols: ["doz"],
  },
  box: {
    symbol: "box",
    name: "Box",
    category: "count",
    conversionFactor: 1,
    commonUse: true,
    alternateSymbols: ["boxes"],
  },
  pack: {
    symbol: "pack",
    name: "Pack",
    category: "count",
    conversionFactor: 1,
    commonUse: true,
    alternateSymbols: ["package", "packages"],
  },
};

// Helper Functions
export const getUnitDefinition = (unit: string): UnitDefinition | null => {
  // Normalize unit to lowercase
  const normalizedUnit = unit.toLowerCase();

  // First try direct lookup
  if (UnitDefinitions[normalizedUnit]) {
    return UnitDefinitions[normalizedUnit];
  }

  // Then try alternate symbols
  for (const [key, def] of Object.entries(UnitDefinitions)) {
    if (def.alternateSymbols?.includes(normalizedUnit)) {
      return def;
    }
  }

  return null;
};

export const getUnitCategory = (unit: string): UnitCategory | null => {
  const definition = getUnitDefinition(unit);
  return definition?.category || null;
};

export const areUnitsCompatible = (unit1: string, unit2: string): boolean => {
  const cat1 = getUnitCategory(unit1);
  const cat2 = getUnitCategory(unit2);
  return cat1 !== null && cat1 === cat2;
};

export const getCommonUnits = (category: UnitCategory): UnitDefinition[] => {
  return Object.values(UnitDefinitions).filter(
    (def) => def.category === category && def.commonUse
  );
};

export const convertUnits = (
  value: number,
  fromUnit: string,
  toUnit: string
): number | null => {
  const fromDef = getUnitDefinition(fromUnit);
  const toDef = getUnitDefinition(toUnit);

  if (!fromDef || !toDef || fromDef.category !== toDef.category) {
    return null;
  }

  // Convert to base unit first, then to target unit
  const baseValue = value * fromDef.conversionFactor;
  return baseValue / toDef.conversionFactor;
};

export const formatUnitValue = (value: number, unit: string): string => {
  const unitDef = getUnitDefinition(unit);
  if (!unitDef) return `${value} ${unit}`;

  // Format number based on magnitude
  let formattedValue = value;
  if (value >= 1000) {
    formattedValue = Math.round(value);
  } else if (value >= 100) {
    formattedValue = Math.round(value * 10) / 10;
  } else if (value >= 10) {
    formattedValue = Math.round(value * 100) / 100;
  } else {
    formattedValue = Math.round(value * 1000) / 1000;
  }

  return `${formattedValue} ${unitDef.symbol}`;
};

export const suggestAlternativeUnit = (
  value: number,
  unit: string
): { value: number; unit: string } | null => {
  const unitDef = getUnitDefinition(unit);
  if (!unitDef) return null;

  const category = unitDef.category;
  const commonUnits = getCommonUnits(category);

  // Convert to all common units and find the best one
  let bestUnit = unit;
  let bestValue = value;
  let bestScore = scoreUnitValue(value);

  commonUnits.forEach((commonUnit) => {
    const convertedValue = convertUnits(value, unit, commonUnit.symbol);
    if (convertedValue !== null) {
      const score = scoreUnitValue(convertedValue);
      if (score > bestScore) {
        bestScore = score;
        bestUnit = commonUnit.symbol;
        bestValue = convertedValue;
      }
    }
  });

  if (bestUnit === unit) return null;

  return {
    value: Math.round(bestValue * 10000) / 10000,
    unit: bestUnit,
  };
};

// Helper function to score how "nice" a number is (1-100 is ideal)
const scoreUnitValue = (value: number): number => {
  if (value === 0) return 0;
  if (value < 0.001 || value > 10000) return 0;
  if (value >= 1 && value <= 100) return 100;
  if (value > 0.1 && value < 1000) return 75;
  if (value > 0.01 && value < 10000) return 50;
  return 25;
};
